import React, { useState, useEffect } from 'react';

const NewsIntel = () => {
  const [signals, setSignals] = useState([]);
  const [blocklist, setBlocklist] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const fetchAll = async () => {
    try {
      const [s, b, f] = await Promise.all([
        fetch('/sectortrade/news-signals').then(r => r.json()),
        fetch('/sectortrade/news-blocklist').then(r => r.json()),
        fetch('/sectortrade/news-feed?limit=50').then(r => r.json())
      ]);
      setSignals(s.signals || []);
      setBlocklist(b.blocklist || []);
      setFeed(f.news || []);
    } catch (err) {
      console.error('NewsIntel error:', err);
    } finally {
      setLoading(false);
    }
  };

  const override = async (ticker) => {
    const reason = prompt(`Override block on ${ticker}?\nReason:`);
    if (!reason) return;
    try {
      await fetch(`/sectortrade/news-override/${ticker}?reason=${encodeURIComponent(reason)}`, {method: 'POST'});
      fetchAll();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 60000);
    return () => clearInterval(iv);
  }, []);

  const sentimentStyle = (s) => ({
    background: s === 'POSITIVE' ? '#f6ffed' : s === 'NEGATIVE' ? '#fff1f0' : '#fafafa',
    color: s === 'POSITIVE' ? '#389e0d' : s === 'NEGATIVE' ? '#cf1322' : '#595959',
    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    border: `1px solid ${s === 'POSITIVE' ? '#b7eb8f' : s === 'NEGATIVE' ? '#ffa39e' : '#d9d9d9'}`
  });

  const scopeStyle = (sc) => ({
    background: sc === 'STOCK' ? '#e6f7ff' : '#fff7e6',
    color: sc === 'STOCK' ? '#0050b3' : '#ad6800',
    padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600
  });

  const fmtTime = (ts) => ts ? new Date(ts).toLocaleString('en-IN', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}) : '—';

  if (loading) return <div style={{padding: 20}}>Loading News Intelligence...</div>;

  const filteredFeed = tab === 'all' ? feed :
                       tab === 'positive' ? feed.filter(n => n.sentiment === 'POSITIVE') :
                       tab === 'negative' ? feed.filter(n => n.sentiment === 'NEGATIVE') :
                       tab === 'stock' ? feed.filter(n => n.scope === 'STOCK') :
                       feed;

  return (
    <div style={{padding: 20, fontFamily: 'system-ui'}}>
      <div style={{background: '#f0f5ff', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #adc6ff'}}>
        <div style={{fontWeight: 600, color: '#1d39c4'}}>News Intelligence</div>
        <div style={{fontSize: 13, color: '#555', marginTop: 4}}>
          Auto-classified news with sentiment (POSITIVE/NEGATIVE) and scope (STOCK/SECTOR). Blocklist and positive signals are DATA-ONLY for the first week — strategies do NOT enforce them yet. Analyze after 7 days to decide on enforcement.
        </div>
      </div>

      {/* Blocklist */}
      <div style={{background: '#fff', padding: 16, border: '1px solid #ddd', borderRadius: 8, marginBottom: 16}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
          <div style={{fontWeight: 600, color: '#cf1322'}}>
            Auto-Blocked Tickers ({blocklist.length}) — negative news, would be excluded if enforced
          </div>
        </div>
        {blocklist.length === 0 ? (
          <div style={{color: '#888', fontSize: 13}}>No blocked tickers today</div>
        ) : (
          <table style={{width: '100%', fontSize: 13, borderCollapse: 'collapse'}}>
            <thead style={{background: '#fafafa'}}>
              <tr>
                <th style={{padding: 8, textAlign: 'left'}}>Ticker</th>
                <th style={{padding: 8, textAlign: 'left'}}>Reason</th>
                <th style={{padding: 8, textAlign: 'left'}}>News</th>
                <th style={{padding: 8, textAlign: 'left'}}>Source</th>
                <th style={{padding: 8, textAlign: 'left'}}>Status</th>
                <th style={{padding: 8, textAlign: 'left'}}>Action</th>
              </tr>
            </thead>
            <tbody>
              {blocklist.map((b, i) => (
                <tr key={i} style={{borderTop: '1px solid #eee', background: b.manually_overridden ? '#f6ffed' : '#fff'}}>
                  <td style={{padding: 8, fontWeight: 600}}>{b.ticker}</td>
                  <td style={{padding: 8, fontSize: 12, color: '#666'}}>{b.reason}</td>
                  <td style={{padding: 8, fontSize: 12, maxWidth: 400}}>{b.news_title?.substring(0, 100)}...</td>
                  <td style={{padding: 8, fontSize: 11}}>{b.news_source}</td>
                  <td style={{padding: 8}}>
                    {b.manually_overridden ? (
                      <span style={{color: '#389e0d', fontSize: 11, fontWeight: 600}}>OVERRIDDEN</span>
                    ) : (
                      <span style={{color: '#cf1322', fontSize: 11, fontWeight: 600}}>BLOCKED</span>
                    )}
                  </td>
                  <td style={{padding: 8}}>
                    {!b.manually_overridden && (
                      <button onClick={() => override(b.ticker)}
                        style={{padding: '4px 10px', background: '#fff', border: '1px solid #389e0d', color: '#389e0d', borderRadius: 4, cursor: 'pointer', fontSize: 11}}>
                        Override
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Positive signals */}
      <div style={{background: '#fff', padding: 16, border: '1px solid #ddd', borderRadius: 8, marginBottom: 16}}>
        <div style={{fontWeight: 600, color: '#389e0d', marginBottom: 12}}>
          Positive Signal Tickers ({signals.length}) — would receive conviction bonus if enforced
        </div>
        {signals.length === 0 ? (
          <div style={{color: '#888', fontSize: 13}}>No positive signals today</div>
        ) : (
          <table style={{width: '100%', fontSize: 13, borderCollapse: 'collapse'}}>
            <thead style={{background: '#fafafa'}}>
              <tr>
                <th style={{padding: 8, textAlign: 'left'}}>Ticker</th>
                <th style={{padding: 8, textAlign: 'left'}}>News</th>
                <th style={{padding: 8, textAlign: 'left'}}>Source</th>
                <th style={{padding: 8, textAlign: 'left'}}>Published</th>
              </tr>
            </thead>
            <tbody>
              {signals.map((s, i) => (
                <tr key={i} style={{borderTop: '1px solid #eee', background: i % 2 ? '#f6ffed' : '#fff'}}>
                  <td style={{padding: 8, fontWeight: 600, color: '#389e0d'}}>{s.ticker}</td>
                  <td style={{padding: 8, fontSize: 12, maxWidth: 500}}>{s.news_title?.substring(0, 120)}</td>
                  <td style={{padding: 8, fontSize: 11}}>{s.news_source}</td>
                  <td style={{padding: 8, fontSize: 11, color: '#666'}}>{fmtTime(s.news_published_ts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Full feed with filter */}
      <div style={{background: '#fff', padding: 16, border: '1px solid #ddd', borderRadius: 8}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
          <div style={{fontWeight: 600}}>News Feed ({filteredFeed.length})</div>
          <div style={{display: 'flex', gap: 8}}>
            {['all', 'positive', 'negative', 'stock'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{
                  padding: '4px 12px',
                  background: tab === t ? '#1890ff' : '#fff',
                  color: tab === t ? '#fff' : '#1890ff',
                  border: '1px solid #1890ff',
                  borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 500,
                  textTransform: 'capitalize'
                }}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div style={{maxHeight: 500, overflowY: 'auto'}}>
          <table style={{width: '100%', fontSize: 13, borderCollapse: 'collapse'}}>
            <thead style={{background: '#fafafa', position: 'sticky', top: 0}}>
              <tr>
                <th style={{padding: 8, textAlign: 'left', width: 90}}>Sentiment</th>
                <th style={{padding: 8, textAlign: 'left', width: 70}}>Scope</th>
                <th style={{padding: 8, textAlign: 'left', width: 110}}>Ticker/Sector</th>
                <th style={{padding: 8, textAlign: 'left'}}>Headline</th>
                <th style={{padding: 8, textAlign: 'left', width: 110}}>Published</th>
                <th style={{padding: 8, textAlign: 'left', width: 100}}>Source</th>
              </tr>
            </thead>
            <tbody>
              {filteredFeed.map((n, i) => (
                <tr key={n.id} style={{borderTop: '1px solid #eee', background: i % 2 ? '#fafafa' : '#fff'}}>
                  <td style={{padding: 8}}><span style={sentimentStyle(n.sentiment)}>{n.sentiment}</span></td>
                  <td style={{padding: 8}}><span style={scopeStyle(n.scope)}>{n.scope}</span></td>
                  <td style={{padding: 8, fontSize: 12, fontWeight: 500}}>
                    {n.impacted_ticker || (n.top_sector ? n.top_sector.replace('NIFTY_', '') : '—')}
                  </td>
                  <td style={{padding: 8, fontSize: 12, maxWidth: 500}}>{n.title?.substring(0, 120)}</td>
                  <td style={{padding: 8, fontSize: 11, color: '#666'}}>{fmtTime(n.published_ts)}</td>
                  <td style={{padding: 8, fontSize: 10, color: '#666'}}>{n.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NewsIntel;
